import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  fetchTier1Catalogs, createTier1Catalog, updateTier1Catalog, deleteTier1Catalog,
  fetchTier1BaseWorks,
  fetchTier1WorkGroups, createTier1WorkGroup, updateTier1WorkGroup, deleteTier1WorkGroup,
  createTier1BaseWork, updateTier1BaseWork, deleteTier1BaseWork,
  fetchTier1PriceLists, createTier1PriceList,
  fetchTier1MainTable, saveTier1MainTable,
  fetchCatalogRequirements, bulkCatalogRequirements,
} from "../api/tier1Api";
import {
  writeCatalogMainTable, readCatalogMainTable,
  writeRequirementsToExcel, readRequirementsFromExcel,
} from "../excel/tier1Sheets";
import { fetchUnits } from "../api/resourceCatalogApi";
import BaseWorkRequirementsModal from "./BaseWorkRequirementsModal";
import { confirmDialog } from "../utils/confirmDialog";

// ── Helpers ───────────────────────────────────────────────────────────────────

const LANGS = [
  { id: "geo", label: "ქართ." },
  { id: "eng", label: "ENG" },
  { id: "rus", label: "РУС" },
];

const CYRILLIC_TO_LATIN = { м: "m", с: "c", к: "k", г: "g", т: "t", л: "l", ч: "h" };

const normalizeUnit = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/²/g, "2")
    .replace(/³/g, "3")
    .replace(/\s+/g, "")
    .replace(/[мскгтлч]/g, (ch) => CYRILLIC_TO_LATIN[ch]);

// Maps legacy free-text units ("м3", "m³") onto a catalog unit code
const matchUnitCode = (value, units) => {
  if (!value) return "";
  const target = normalizeUnit(value);
  const hit = units.find((u) =>
    [u.code, u.symbol, u.name_eng, u.name_rus].some((n) => n && normalizeUnit(n) === target)
  );
  return hit ? hit.code : "";
};

// Segment-aware: 1-12 < 1-118 < 11-3 (Postgres ORDER BY ignores hyphens under locale collation)
const codeCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
const sortByCode = (items) => [...items].sort((a, b) => codeCollator.compare(a.code || "", b.code || ""));

const bwName = (bw, lang) =>
  bw[`name_${lang}`] || bw.name_geo || bw.name_eng || bw.name_rus || "";

const wgName = (wg, lang) =>
  wg[`name_${lang}`] || wg[`description_${lang}`] ||
  wg.name_geo || wg.description_geo ||
  wg.name_eng || wg.description_eng ||
  wg.name_rus || wg.description_rus || "";

function buildLevelTree(bws, lang) {
  const direct = [];
  const l1map = new Map();
  for (const bw of bws) {
    const l1 = bw[`level1_value_${lang}`] || bw.level1_value_geo || "";
    if (!l1) { direct.push(bw); continue; }
    if (!l1map.has(l1)) l1map.set(l1, { label: l1, direct: [], l2map: new Map() });
    const l1node = l1map.get(l1);
    const l2 = bw[`level2_value_${lang}`] || bw.level2_value_geo || "";
    if (!l2) { l1node.direct.push(bw); continue; }
    if (!l1node.l2map.has(l2)) l1node.l2map.set(l2, { label: l2, works: [] });
    l1node.l2map.get(l2).works.push(bw);
  }
  return { direct, l1nodes: [...l1map.values()] };
}

// ── ReqChips ──────────────────────────────────────────────────────────────────

const chipSt = (bg) => ({
  background: bg, color: "#fff", fontSize: 10, fontWeight: 700,
  padding: "1px 5px", borderRadius: 3,
});

function ReqChips({ counts = {} }) {
  const l = counts.LABOUR || 0, e = counts.EQUIPMENT || 0, m = counts.MATERIAL || 0;
  const total = l + e + m;
  if (!total) return <span style={{ fontSize: 9, color: "#64748b", fontStyle: "italic" }}>no reqs</span>;
  return (
    <span style={{ display: "flex", gap: 3, alignItems: "center" }}>
      {l > 0 && <span style={chipSt("#2196F3")}>L {l}</span>}
      {e > 0 && <span style={chipSt("#FF9800")}>E {e}</span>}
      {m > 0 && <span style={chipSt("#4CAF50")}>M {m}</span>}
    </span>
  );
}

// ── WorkGroupEditModal ────────────────────────────────────────────────────────

function WorkGroupEditModal({ wg, catalogId, tier, token, onSave, onClose }) {
  const isNew = !wg?.id;
  const [form, setForm] = useState({
    code: wg?.code || "",
    name_geo: wg?.name_geo || "",
    name_eng: wg?.name_eng || "",
    name_rus: wg?.name_rus || "",
    level1_param_geo: wg?.level1_param_geo || "",
    level1_param_eng: wg?.level1_param_eng || "",
    level2_param_geo: wg?.level2_param_geo || "",
    level2_param_eng: wg?.level2_param_eng || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.code.trim()) { setErr("Code required"); return; }
    setBusy(true); setErr("");
    try {
      const result = isNew
        ? await createTier1WorkGroup(token, catalogId, form, tier)
        : await updateTier1WorkGroup(token, catalogId, wg.id, form, tier);
      onSave(result);
    } catch (ex) { setErr(ex.message || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, padding: 20, width: "min(460px,90vw)" }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 13 }}>{isNew ? "Add Work Group" : "Edit Work Group"}</h3>
        {err && <div className="error" style={{ marginBottom: 8 }}>{err}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          <input placeholder="Code *  (e.g. 06-01-001)" value={form.code} onChange={set("code")} />
          <input placeholder="Name (GEO)" value={form.name_geo} onChange={set("name_geo")} />
          <input placeholder="Name (ENG)" value={form.name_eng} onChange={set("name_eng")} />
          <input placeholder="Name (RUS)" value={form.name_rus} onChange={set("name_rus")} />
          <div style={{ display: "flex", gap: 6 }}>
            <input placeholder="Level 1 param label (GEO)" value={form.level1_param_geo} onChange={set("level1_param_geo")} style={{ flex: 1 }} />
            <input placeholder="Level 1 param label (ENG)" value={form.level1_param_eng} onChange={set("level1_param_eng")} style={{ flex: 1 }} />
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <input placeholder="Level 2 param label (GEO)" value={form.level2_param_geo} onChange={set("level2_param_geo")} style={{ flex: 1 }} />
            <input placeholder="Level 2 param label (ENG)" value={form.level2_param_eng} onChange={set("level2_param_eng")} style={{ flex: 1 }} />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
          <button className="boq-action-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="primary" onClick={submit} disabled={busy || !form.code.trim()}>{isNew ? "Create" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

// ── BaseWorkEditModal ─────────────────────────────────────────────────────────

function BaseWorkEditModal({ bw, workGroupId, catalogId, tier, token, prefill = {}, focusLevel1 = false, focusLevel2 = false, onSave, onClose }) {
  const isNew = !bw?.id;
  const [form, setForm] = useState({
    code: bw?.code || "",
    work_group_id: workGroupId,
    name_geo: bw?.name_geo || "",
    name_eng: bw?.name_eng || "",
    name_rus: bw?.name_rus || "",
    level1_value_geo: bw?.level1_value_geo ?? prefill.level1_value_geo ?? "",
    level2_value_geo: bw?.level2_value_geo ?? prefill.level2_value_geo ?? "",
    base_unit: bw?.base_unit || "",
    base_unit_value: bw?.base_unit_value ?? 1,
    unit_rate: bw?.unit_rate ?? "",
    currency: bw?.currency || "GEL",
    gesn_code: bw?.gesn_code || "",
    notes: bw?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [units, setUnits] = useState([]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    fetchUnits(token).then((list) => setUnits(list || [])).catch(() => setUnits([]));
  }, [token]);

  useEffect(() => {
    const matched = matchUnitCode(form.base_unit, units);
    if (matched && matched !== form.base_unit) setForm((f) => ({ ...f, base_unit: matched }));
  }, [units]);

  const unitsByDimension = useMemo(() => {
    const groups = new Map();
    units.forEach((u) => {
      if (!groups.has(u.dimension)) groups.set(u.dimension, []);
      groups.get(u.dimension).push(u);
    });
    return [...groups.entries()];
  }, [units]);

  const isLegacyUnit = Boolean(form.base_unit) && !units.some((u) => u.code === form.base_unit);

  const submit = async () => {
    if (!form.code.trim()) { setErr("Code required"); return; }
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        base_unit_value: form.base_unit_value !== "" ? Number(form.base_unit_value) : 1,
        unit_rate: form.unit_rate !== "" ? Number(form.unit_rate) : null,
      };
      const result = isNew
        ? await createTier1BaseWork(token, catalogId, payload, tier)
        : await updateTier1BaseWork(token, bw.id, payload, tier);
      onSave(result);
    } catch (ex) { setErr(ex.message || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, padding: 20, width: "min(460px,90vw)", maxHeight: "90vh", overflow: "auto" }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 13 }}>{isNew ? "Add Base Work" : "Edit Base Work"}</h3>
        {err && <div className="error" style={{ marginBottom: 8 }}>{err}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          <input placeholder="Code *  (e.g. 06-01-001-02)" value={form.code} onChange={set("code")} />
          <input placeholder="Name (GEO)" value={form.name_geo} onChange={set("name_geo")} />
          <input placeholder="Name (ENG)" value={form.name_eng} onChange={set("name_eng")} />
          <input placeholder="Name (RUS)" value={form.name_rus} onChange={set("name_rus")} />
          <div style={{ display: "flex", gap: 6 }}>
            <input
              placeholder={focusLevel1 ? "Folder name (Level 1) *" : "Level 1 value"}
              value={form.level1_value_geo}
              onChange={set("level1_value_geo")}
              style={{ flex: 1, ...(focusLevel1 ? { borderColor: "#06b6d4", outline: "1px solid #06b6d4" } : {}) }}
              autoFocus={focusLevel1}
            />
            <input
              placeholder={focusLevel2 ? "Sub-folder name (Level 2) *" : "Level 2 value"}
              value={form.level2_value_geo}
              onChange={set("level2_value_geo")}
              style={{ flex: 1, ...(focusLevel2 ? { borderColor: "#06b6d4", outline: "1px solid #06b6d4" } : {}) }}
              autoFocus={focusLevel2}
            />
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <select value={form.base_unit} onChange={set("base_unit")} style={{ flex: 1 }}>
              <option value="">— Base unit —</option>
              {isLegacyUnit && <option value={form.base_unit}>{form.base_unit} (legacy)</option>}
              {unitsByDimension.map(([dimension, list]) => (
                <optgroup key={dimension} label={dimension}>
                  {list.map((u) => (
                    <option key={u.code} value={u.code}>
                      {u.code} — {u.symbol || u.name_geo || ""}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <input type="number" placeholder="Qty" value={form.base_unit_value} onChange={set("base_unit_value")} style={{ width: 70 }} />
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <input type="number" placeholder="Unit rate" value={form.unit_rate} onChange={set("unit_rate")} style={{ flex: 1 }} />
            <input placeholder="Currency" value={form.currency} onChange={set("currency")} style={{ width: 60 }} />
          </div>
          <input placeholder="GESN code" value={form.gesn_code} onChange={set("gesn_code")} />
          <input placeholder="Notes" value={form.notes} onChange={set("notes")} />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
          <button className="boq-action-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="primary" onClick={submit} disabled={busy || !form.code.trim()}>{isNew ? "Create" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Tier1Panel({ token, isSuperuser = false, editableCatalogIds = [], tier = 1 }) {
  const [catalogs, setCatalogs] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [newCatalogName, setNewCatalogName] = useState("");

  const [workGroups, setWorkGroups] = useState([]);
  const [expandedWgs, setExpandedWgs] = useState(new Set());
  const [wgBaseWorks, setWgBaseWorks] = useState({});
  const [loadingWgs, setLoadingWgs] = useState(new Set());
  const [openNodes, setOpenNodes] = useState({});
  const [lang, setLang] = useState("geo");

  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  const [editingWg, setEditingWg] = useState(null);
  const [editingBw, setEditingBw] = useState(null);
  const [reqsForBw, setReqsForBw] = useState(null);
  const [renamingCatalog, setRenamingCatalog] = useState(false);
  const [renameValue, setRenameValue] = useState("");

  const [priceLists, setPriceLists] = useState([]);
  const [activePlId, setActivePlId] = useState("");
  const [newPlName, setNewPlName] = useState("");
  const [newPlCurrency, setNewPlCurrency] = useState("GEL");

  const selectedCatalog = useMemo(
    () => catalogs.find((c) => String(c.id) === String(selectedId)) || null,
    [catalogs, selectedId]
  );
  const canEdit = isSuperuser || Boolean(selectedCatalog && editableCatalogIds.includes(selectedCatalog.id));
  const clr = () => { setStatus(""); setError(""); };

  // ── Load catalogs ──
  const loadCatalogs = useCallback(async () => {
    if (!token) return;
    try {
      const list = await fetchTier1Catalogs(token, tier);
      setCatalogs(list || []);
    } catch (ex) { setError(ex.message || "Failed to load catalogs"); }
  }, [token, tier]);

  useEffect(() => {
    setSelectedId(""); setWorkGroups([]); setExpandedWgs(new Set());
    setWgBaseWorks({}); setPriceLists([]); setActivePlId("");
    setSearch(""); setSearchResults(null);
  }, [tier]);

  useEffect(() => { loadCatalogs(); }, [loadCatalogs]);

  // ── Load work groups ──
  const loadWorkGroups = useCallback(async () => {
    if (!token || !selectedId) { setWorkGroups([]); return; }
    try {
      const wgs = await fetchTier1WorkGroups(token, selectedId, tier);
      setWorkGroups(wgs || []);
      setExpandedWgs(new Set()); setWgBaseWorks({}); setOpenNodes({});
    } catch (ex) { setError(ex.message || "Failed to load work groups"); }
  }, [token, selectedId, tier]);

  useEffect(() => { loadWorkGroups(); }, [loadWorkGroups]);

  // ── Load price lists ──
  const loadPriceLists = useCallback(async () => {
    if (!token || !selectedId) { setPriceLists([]); return; }
    try {
      const list = await fetchTier1PriceLists(token, selectedId, tier);
      setPriceLists(list || []);
      if (!activePlId && list && list.length) setActivePlId(String(list[0].id));
    } catch { setPriceLists([]); }
  }, [token, selectedId, tier, activePlId]);

  useEffect(() => { loadPriceLists(); }, [loadPriceLists]);

  // ── Search ──
  useEffect(() => {
    if (!search.trim() || !selectedId) { setSearchResults(null); return; }
    const q = search.trim();
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchTier1BaseWorks(token, selectedId, { q, limit: 200, tier });
        if (!cancelled) setSearchResults(res || []);
      } catch { if (!cancelled) setSearchResults([]); }
    })();
    return () => { cancelled = true; };
  }, [search, selectedId, token, tier]);

  // ── Tree toggle ──
  const toggleWg = async (wg) => {
    const id = wg.id;
    if (expandedWgs.has(id)) {
      setExpandedWgs((prev) => { const n = new Set(prev); n.delete(id); return n; });
      return;
    }
    setExpandedWgs((prev) => new Set(prev).add(id));
    if (!wgBaseWorks[id]) {
      setLoadingWgs((prev) => new Set(prev).add(id));
      try {
        const bws = await fetchTier1BaseWorks(token, selectedId, { groupCode: wg.code, limit: 2000, tier });
        setWgBaseWorks((prev) => ({ ...prev, [id]: bws || [] }));
      } catch (ex) { setError(ex.message || "Failed"); }
      finally { setLoadingWgs((prev) => { const n = new Set(prev); n.delete(id); return n; }); }
    }
  };

  const handleRefresh = useCallback(async () => {
    if (!token || !selectedId) return;
    clr(); setBusy(true);
    try {
      const wgs = await fetchTier1WorkGroups(token, selectedId, tier);
      setWorkGroups(wgs || []);
      const reloads = [...expandedWgs].map(async (wgId) => {
        const wg = (wgs || []).find((w) => w.id === wgId);
        if (!wg) return;
        const bws = await fetchTier1BaseWorks(token, selectedId, { groupCode: wg.code, limit: 2000, tier });
        setWgBaseWorks((prev) => ({ ...prev, [wgId]: bws || [] }));
      });
      await Promise.all(reloads);
    } catch (ex) { setError(ex.message || "Failed to refresh"); }
    finally { setBusy(false); }
  }, [token, selectedId, tier, expandedWgs]);

  const refreshWg = useCallback(async (wgId) => {
    const wg = workGroups.find((w) => w.id === wgId);
    if (!wg) return;
    setLoadingWgs((prev) => new Set(prev).add(wgId));
    try {
      const bws = await fetchTier1BaseWorks(token, selectedId, { groupCode: wg.code, limit: 2000, tier });
      setWgBaseWorks((prev) => ({ ...prev, [wgId]: bws || [] }));
    } catch { }
    finally { setLoadingWgs((prev) => { const n = new Set(prev); n.delete(wgId); return n; }); }
  }, [workGroups, token, selectedId, tier]);

  const toggleNode = (key) => setOpenNodes((prev) => ({ ...prev, [key]: !prev[key] }));

  // ── Catalog CRUD ──
  const handleCreateCatalog = async () => {
    clr(); if (!newCatalogName.trim()) { setError("Name required"); return; }
    setBusy(true);
    try {
      const cat = await createTier1Catalog(token, { name: newCatalogName.trim() }, tier);
      setNewCatalogName(""); await loadCatalogs(); setSelectedId(String(cat.id));
      setStatus(`Catalog "${cat.name}" created.`);
    } catch (ex) { setError(ex.message || "Failed"); }
    finally { setBusy(false); }
  };

  const handleDeleteCatalog = async () => {
    if (!selectedCatalog) return;
    if (!(await confirmDialog(`Delete catalog "${selectedCatalog.name}" and all its data?`))) return;
    setBusy(true); clr();
    try {
      await deleteTier1Catalog(token, selectedCatalog.id, tier);
      setSelectedId(""); await loadCatalogs(); setStatus("Catalog deleted.");
    } catch (ex) { setError(ex.message || "Failed"); }
    finally { setBusy(false); }
  };

  // ── Work group CRUD ──
  const handleDeleteWg = async (wg) => {
    if (!(await confirmDialog(`Delete work group "${wg.code}" and all its base works?`))) return;
    setBusy(true); clr();
    try {
      await deleteTier1WorkGroup(token, selectedId, wg.id, tier);
      await loadWorkGroups(); setStatus(`Work group "${wg.code}" deleted.`);
    } catch (ex) { setError(ex.message || "Failed"); }
    finally { setBusy(false); }
  };

  // ── Base work CRUD ──
  const handleDeleteBw = async (bw) => {
    if (!(await confirmDialog(`Delete base work "${bw.code}"?`))) return;
    setBusy(true); clr();
    try {
      await deleteTier1BaseWork(token, bw.id, tier);
      await refreshWg(bw.work_group_id); setStatus(`Base work "${bw.code}" deleted.`);
    } catch (ex) { setError(ex.message || "Failed"); }
    finally { setBusy(false); }
  };

  // ── Excel bulk ──
  const handleLoadCatalogTable = async () => {
    if (!selectedCatalog) return; setBusy(true); clr();
    try {
      const data = await fetchTier1MainTable(token, selectedCatalog.id, tier);
      const sheetName = await writeCatalogMainTable(selectedCatalog.name, data?.rows || [], data?.headers || null);
      const count = data?.rows?.length ?? 0;
      setStatus(count > 0 ? `Loaded ${count} row(s) into "${sheetName}".` : `Empty template into "${sheetName}".`);
    } catch (ex) { setError(ex.message || "Failed"); }
    finally { setBusy(false); }
  };

  const handleSaveCatalogTable = async () => {
    if (!selectedCatalog || !canEdit) return; setBusy(true); clr();
    try {
      const rows = await readCatalogMainTable(selectedCatalog.name);
      if (!rows.length) { setError("Sheet is empty."); return; }
      const result = await saveTier1MainTable(token, selectedCatalog.id, rows, tier);
      await loadCatalogs(); await loadWorkGroups();
      const w = result?.written || {};
      setStatus(
        `Saved. ${result?.work_group_count ?? "?"} groups, ${result?.base_work_count ?? "?"} base works ` +
        `(+${w.base_works || 0}/~${w.updated_base_works || 0}/-${w.deleted_base_works || 0}).`
      );
      if (result?.error_count) setError(`${result.error_count} row(s) failed.`);
    } catch (ex) { setError(ex.message || "Failed"); }
    finally { setBusy(false); }
  };

  const handleLoadRequirements = async () => {
    if (!selectedCatalog) return; setBusy(true); clr();
    try {
      const rows = await fetchCatalogRequirements(token, selectedCatalog.id, tier);
      const sheetName = await writeRequirementsToExcel(selectedCatalog.name, rows || []);
      setStatus(`Loaded ${(rows || []).length} requirement(s) into "${sheetName}".`);
    } catch (ex) { setError(ex.message || "Failed"); }
    finally { setBusy(false); }
  };

  const handleSaveRequirements = async () => {
    if (!selectedCatalog || !canEdit) return; setBusy(true); clr();
    try {
      const rows = await readRequirementsFromExcel(selectedCatalog.name);
      if (!rows.length) { setError("Requirements sheet is empty."); return; }
      const result = await bulkCatalogRequirements(token, selectedCatalog.id, rows, tier);
      const w = result?.written || {};
      setStatus(`Requirements: +${w.created || 0}/~${w.updated || 0}/-${w.deleted || 0}.`);
      if (result?.error_count) setError(`${result.error_count} row(s) failed.`);
      await loadWorkGroups();
    } catch (ex) { setError(ex.message || "Failed"); }
    finally { setBusy(false); }
  };

  const handleRenameCatalog = async () => {
    if (!selectedCatalog || !renameValue.trim()) return;
    setBusy(true); clr();
    try {
      const updated = await updateTier1Catalog(token, selectedCatalog.id, { name: renameValue.trim() }, tier);
      await loadCatalogs();
      setRenamingCatalog(false);
      setRenameValue("");
      setStatus(`Catalog renamed to "${updated.name}".`);
    } catch (ex) { setError(ex.message || "Failed"); }
    finally { setBusy(false); }
  };

  const handleCreatePriceList = async () => {
    if (!selectedCatalog || !newPlName.trim()) return; clr(); setBusy(true);
    try {
      const pl = await createTier1PriceList(token, selectedCatalog.id, { name: newPlName.trim(), currency: newPlCurrency || "GEL" }, tier);
      setNewPlName(""); await loadPriceLists(); setActivePlId(String(pl.id));
    } catch (ex) { setError(ex.message || "Failed"); }
    finally { setBusy(false); }
  };

  // ── Render: base work row ──
  const renderBwRow = (bw, indent = 0) => {
    const name = bwName(bw, lang);
    const counts = bw.requirement_counts || {};
    const total = (counts.LABOUR || 0) + (counts.EQUIPMENT || 0) + (counts.MATERIAL || 0);
    const pl = `${8 + indent * 14}px`;
    return (
      <div key={bw.id} style={{ display: "flex", alignItems: "flex-start", gap: 4, padding: `4px 4px 4px ${pl}`, borderBottom: "1px solid #1e293b" }}>
        <span style={{ color: "#94a3b8", fontSize: 11, marginTop: 3 }}>📄</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontFamily: "monospace", fontSize: 11, color: "#67e8f9" }}>{bw.code}</span>
            {bw.base_unit && (
              <span style={{ fontSize: 10, color: "#64748b" }}>{bw.base_unit_value || 1} {bw.base_unit}</span>
            )}
          </div>
          <div style={{ fontSize: 12, color: name ? "#e2e8f0" : "#64748b", fontStyle: name ? "normal" : "italic", marginTop: 1 }}>
            {name || "(no name)"}
          </div>
          <div style={{ display: "flex", gap: 3, marginTop: 3 }}>
            <ReqChips counts={counts} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
          <button
            className={total > 0 ? "primary" : "boq-action-btn"}
            style={{ padding: "2px 6px", fontSize: 10 }}
            onClick={() => setReqsForBw(bw)}
            title="Requirements"
          >📋{total > 0 ? ` ${total}` : " +"}</button>
          {canEdit && (
            <>
              <button className="boq-action-btn" style={{ padding: "2px 6px", fontSize: 10 }} onClick={() => setEditingBw({ bw, wgId: bw.work_group_id })} title="Edit">✎</button>
              <button className="boq-action-btn" style={{ padding: "2px 6px", fontSize: 10, color: "#f87171" }} onClick={() => handleDeleteBw(bw)} title="Delete">✕</button>
            </>
          )}
        </div>
      </div>
    );
  };

  // ── Render: tree ──
  const renderTree = () => {
    if (!workGroups.length) {
      return (
        <div style={{ padding: "16px 8px", color: "#64748b", fontSize: 12 }}>
          No work groups. Use the Excel bulk import below or click "+ Group" to add one.
        </div>
      );
    }
    return sortByCode(workGroups).map((wg) => {
      const isExp = expandedWgs.has(wg.id);
      const isLoading = loadingWgs.has(wg.id);
      const bws = sortByCode(wgBaseWorks[wg.id] || []);
      const { direct, l1nodes } = buildLevelTree(bws, lang);

      return (
        <div key={wg.id} style={{ borderBottom: "1px solid #1e293b" }}>
          {/* Work group header */}
          <div
            style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 4px", background: isExp ? "rgba(6,182,212,0.06)" : "transparent", cursor: "pointer" }}
            onClick={() => toggleWg(wg)}
          >
            <span style={{ color: "#94a3b8", fontSize: 11, width: 16, textAlign: "center", flexShrink: 0 }}>
              {isLoading ? "…" : isExp ? "▾" : "▸"}
            </span>
            <span style={{ fontSize: 13 }}>📁</span>
            <span style={{ fontFamily: "monospace", fontSize: 11, color: "#67e8f9", marginRight: 4 }}>{wg.code}</span>
            <span style={{ fontSize: 12, color: "#e2e8f0", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {wgName(wg, lang)}
            </span>
            <div style={{ display: "flex", gap: 2, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
              {canEdit && isExp && (
                <>
                  <button className="primary" style={{ padding: "2px 6px", fontSize: 10 }} onClick={() => setEditingBw({ bw: null, wgId: wg.id, prefill: {} })} title="Add base work (no folder)">+ Work</button>
                  <button className="boq-action-btn" style={{ padding: "2px 6px", fontSize: 10 }} onClick={() => setEditingBw({ bw: null, wgId: wg.id, prefill: { level1_value_geo: "" }, focusLevel1: true })} title="Add new folder">+ Folder</button>
                </>
              )}
              {canEdit && (
                <>
                  <button className="boq-action-btn" style={{ padding: "2px 6px", fontSize: 10 }} onClick={() => setEditingWg(wg)} title="Edit">✎</button>
                  <button className="boq-action-btn" style={{ padding: "2px 6px", fontSize: 10, color: "#f87171" }} onClick={() => handleDeleteWg(wg)} title="Delete">✕</button>
                </>
              )}
            </div>
          </div>

          {/* Expanded content */}
          {isExp && (
            <div>
              {direct.map((bw) => renderBwRow(bw, 1))}
              {l1nodes.map((l1) => {
                const l1key = `${wg.id}::${l1.label}`;
                const l1open = openNodes[l1key] !== false;
                const l1total = l1.direct.length + [...l1.l2map.values()].reduce((s, v) => s + v.works.length, 0);
                return (
                  <div key={l1key}>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 4px 3px 22px", cursor: "pointer" }}
                      onClick={() => toggleNode(l1key)}
                    >
                      <span style={{ color: "#94a3b8", fontSize: 10 }}>{l1open ? "▾" : "▸"}</span>
                      <span style={{ fontSize: 12 }}>📁</span>
                      {(wg[`level1_param_${lang}`] || wg.level1_param_geo) && (
                        <span style={{ fontSize: 10, color: "#64748b", fontStyle: "italic" }}>
                          {wg[`level1_param_${lang}`] || wg.level1_param_geo}:
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: "#cbd5e1" }}>{l1.label}</span>
                      <span style={{ fontSize: 10, color: "#64748b", marginLeft: 2 }}>({l1total})</span>
                      {canEdit && (
                        <div style={{ display: "flex", gap: 2, marginLeft: "auto" }} onClick={(e) => e.stopPropagation()}>
                          <button className="primary" style={{ padding: "2px 5px", fontSize: 10 }} title="Add work in this folder" onClick={() => setEditingBw({ bw: null, wgId: wg.id, prefill: { level1_value_geo: l1.label } })}>+ Work</button>
                          <button className="boq-action-btn" style={{ padding: "2px 5px", fontSize: 10 }} title="Add sub-folder" onClick={() => setEditingBw({ bw: null, wgId: wg.id, prefill: { level1_value_geo: l1.label, level2_value_geo: "" }, focusLevel2: true })}>+ Sub</button>
                        </div>
                      )}
                    </div>
                    {l1open && (
                      <div>
                        {l1.direct.map((bw) => renderBwRow(bw, 2))}
                        {[...l1.l2map.values()].map((l2) => {
                          const l2key = `${l1key}::${l2.label}`;
                          const l2open = openNodes[l2key] !== false;
                          return (
                            <div key={l2key}>
                              <div
                                style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 4px 3px 36px", cursor: "pointer" }}
                                onClick={() => toggleNode(l2key)}
                              >
                                <span style={{ color: "#94a3b8", fontSize: 10 }}>{l2open ? "▾" : "▸"}</span>
                                <span style={{ fontSize: 12 }}>📁</span>
                                {(wg[`level2_param_${lang}`] || wg.level2_param_geo) && (
                                  <span style={{ fontSize: 10, color: "#64748b", fontStyle: "italic" }}>
                                    {wg[`level2_param_${lang}`] || wg.level2_param_geo}:
                                  </span>
                                )}
                                <span style={{ fontSize: 11, color: "#cbd5e1" }}>{l2.label}</span>
                                <span style={{ fontSize: 10, color: "#64748b", marginLeft: 2 }}>({l2.works.length})</span>
                                {canEdit && (
                                  <div style={{ marginLeft: "auto" }} onClick={(e) => e.stopPropagation()}>
                                    <button className="primary" style={{ padding: "2px 5px", fontSize: 10 }} title="Add work in this sub-folder" onClick={() => setEditingBw({ bw: null, wgId: wg.id, prefill: { level1_value_geo: l1.label, level2_value_geo: l2.label } })}>+ Work</button>
                                  </div>
                                )}
                              </div>
                              {l2open && l2.works.map((bw) => renderBwRow(bw, 3))}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    });
  };

  // ── Render ──
  return (
    <div>
      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}
      {status && <div className="status" style={{ marginBottom: 8 }}>{status}</div>}

      {/* Catalog picker */}
      <div className="panel" style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ minWidth: 80 }}>კატალოგი</label>
          <select
            value={selectedId}
            onChange={(e) => {
              setSelectedId(e.target.value);
              setRenamingCatalog(false); setRenameValue("");
              setWorkGroups([]); setPriceLists([]); setActivePlId("");
              setSearch(""); setSearchResults(null);
            }}
            style={{ flex: "1 1 200px" }}
          >
            <option value="">— select —</option>
            {catalogs.map((c) => (
              <option key={c.id} value={c.id}>{c.name}{c.code ? ` (${c.code})` : ""}</option>
            ))}
          </select>
          {selectedCatalog && isSuperuser && (
            <>
              <button
                className="boq-action-btn"
                style={{ padding: "4px 8px", fontSize: 11 }}
                onClick={() => { setRenamingCatalog((v) => !v); setRenameValue(selectedCatalog.name); }}
                disabled={busy}
                title="Rename catalog"
              >✎</button>
              <button className="boq-action-btn" onClick={handleDeleteCatalog} disabled={busy}>✕ Delete</button>
            </>
          )}
        </div>

        {/* Inline rename */}
        {renamingCatalog && selectedCatalog && (
          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
            <input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="New catalog name"
              style={{ flex: "1 1 200px" }}
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") handleRenameCatalog(); if (e.key === "Escape") setRenamingCatalog(false); }}
            />
            <button className="primary" onClick={handleRenameCatalog} disabled={busy || !renameValue.trim()}>Save name</button>
            <button className="boq-action-btn" onClick={() => setRenamingCatalog(false)} disabled={busy}>Cancel</button>
          </div>
        )}

        {isSuperuser && (
          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
            <input
              placeholder="New catalog name (e.g. GESN, ETIM, Custom)"
              value={newCatalogName}
              onChange={(e) => setNewCatalogName(e.target.value)}
              style={{ flex: "1 1 200px" }}
            />
            <button className="primary" onClick={handleCreateCatalog} disabled={busy || !newCatalogName.trim()}>+ Create</button>
          </div>
        )}
      </div>

      {/* Tree + Search */}
      {selectedCatalog && (
        <div className="panel" style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 3 }}>
              {LANGS.map((l) => (
                <button key={l.id} onClick={() => setLang(l.id)} className={lang === l.id ? "primary" : "boq-action-btn"} style={{ padding: "3px 8px", fontSize: 11 }}>{l.label}</button>
              ))}
            </div>
            <input
              placeholder="ძებნა / search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: "1 1 150px" }}
            />
            <button
              className="boq-action-btn"
              style={{ padding: "3px 8px", fontSize: 11 }}
              onClick={handleRefresh}
              disabled={busy}
              title="Refresh tree"
            >↻</button>
            {canEdit && !search.trim() && (
              <button className="primary" style={{ padding: "3px 8px", fontSize: 11 }} onClick={() => setEditingWg({})}>+ Group</button>
            )}
          </div>
          <div style={{ maxHeight: 360, overflow: "auto", border: "1px solid #1e293b", borderRadius: 4 }}>
            {search.trim()
              ? searchResults === null
                ? <div style={{ padding: "12px 8px", color: "#64748b", fontSize: 12 }}>Searching…</div>
                : searchResults.length === 0
                  ? <div style={{ padding: "12px 8px", color: "#64748b", fontSize: 12 }}>No results.</div>
                  : sortByCode(searchResults).map((bw) => renderBwRow(bw, 0))
              : renderTree()
            }
          </div>
        </div>
      )}

      {/* Excel bulk */}
      {selectedCatalog && (
        <div className="panel" style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", marginBottom: 8 }}>Excel bulk import / export</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
            <button className="boq-action-btn" onClick={handleLoadCatalogTable} disabled={busy}>↧ Load catalog table</button>
            {canEdit && <button className="primary" onClick={handleSaveCatalogTable} disabled={busy}>✓ Save catalog table</button>}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10 }}>Work groups + base works in one sheet.</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
            <button className="boq-action-btn" onClick={handleLoadRequirements} disabled={busy}>↧ Load requirements</button>
            {canEdit && <button className="primary" onClick={handleSaveRequirements} disabled={busy}>✓ Save requirements</button>}
          </div>
          <div style={{ fontSize: 11, color: "#64748b" }}>
            All LABOUR / EQUIPMENT / MATERIAL requirements for all base works.
          </div>
        </div>
      )}

      {/* Price list */}
      {selectedCatalog && (
        <div className="panel" style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ minWidth: 80 }}>ფასი / Price list</label>
            <select value={activePlId} onChange={(e) => setActivePlId(e.target.value)} style={{ flex: "1 1 180px" }}>
              <option value="">— none —</option>
              {priceLists.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.currency || "GEL"})</option>)}
            </select>
          </div>
          {canEdit && (
            <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
              <input placeholder="New price list name…" value={newPlName} onChange={(e) => setNewPlName(e.target.value)} style={{ flex: "1 1 180px" }} />
              <input value={newPlCurrency} onChange={(e) => setNewPlCurrency(e.target.value)} style={{ width: 60 }} />
              <button className="boq-action-btn" onClick={handleCreatePriceList} disabled={busy || !newPlName.trim()}>+ Add</button>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {editingWg !== null && (
        <WorkGroupEditModal
          wg={editingWg}
          catalogId={selectedId}
          tier={tier}
          token={token}
          onSave={(saved) => { setEditingWg(null); loadWorkGroups(); setStatus(`Work group "${saved.code}" saved.`); }}
          onClose={() => setEditingWg(null)}
        />
      )}
      {editingBw !== null && (
        <BaseWorkEditModal
          bw={editingBw.bw}
          workGroupId={editingBw.wgId}
          catalogId={selectedId}
          tier={tier}
          token={token}
          prefill={editingBw.prefill || {}}
          focusLevel1={!!editingBw.focusLevel1}
          focusLevel2={!!editingBw.focusLevel2}
          onSave={(saved) => { setEditingBw(null); refreshWg(saved.work_group_id); setStatus(`Base work "${saved.code}" saved.`); }}
          onClose={() => setEditingBw(null)}
        />
      )}
      {reqsForBw && (
        <BaseWorkRequirementsModal
          token={token}
          baseWork={reqsForBw}
          tier={tier}
          isSuperuser={canEdit}
          onClose={() => { setReqsForBw(null); if (reqsForBw.work_group_id) refreshWg(reqsForBw.work_group_id); }}
        />
      )}
    </div>
  );
}
